// Product analytics for the retention test (research open questions #2–#4).
// Opt-in only (allergy/diet data is special-category under GDPR, so we never send it).
// Events are batched to the snapcook_events table when a backend is configured.

import { useStore } from '../state/store';
import { config, hasBackend } from './config';
import { getAccessToken, getSupabase } from './supabase';

export type EventName =
  | 'app_open' | 'onboarding_done' | 'scan_started' | 'scan_completed' | 'scan_failed' | 'review_done'
  | 'recipe_shown' | 'picks_shuffled' | 'tonight_pick_opened' | 'recipe_opened' | 'cook_started' | 'cook_finished' | 'recipe_saved'
  | 'plan_created' | 'plan_swapped' | 'list_created' | 'list_shared' | 'instacart_handoff' | 'sample_fridge_used'
  // Onboarding funnel and first value
  | 'onboarding_started' | 'onboarding_step' | 'summary_viewed' | 'first_meal_shown' | 'first_meal_opened'
  | 'more_meals_shown' | 'more_meals_dismissed' | 'more_meals_explore' | 'returned_after_onboarding'
  // Progressive personalization
  | 'prompt_shown' | 'prompt_answered' | 'prompt_dismissed'
  // Accounts
  | 'account_prompt_shown' | 'account_prompt_dismissed' | 'account_created'
  // Reminders (native app)
  | 'reminders_enabled' | 'reminders_dismissed' | 'reminder_opened'
  // Amari Plus and Ask Amari
  | 'plus_viewed' | 'plus_interest' | 'recipe_unlocked' | 'ask_amari_sent' | 'recipe_written' | 'ask_amari_offer_taken';

type Props = Record<string, string | number | boolean | null>;
interface Queued { name: EventName; props: Props; occurred_at: string }

const queue: Queued[] = [];
let flushTimer: ReturnType<typeof setTimeout> | undefined;

export function track(name: EventName, props: Props = {}): void {
  if (import.meta.env.DEV) console.debug('[track]', name, props);
  if (!useStore.getState().consent.analytics || !hasBackend) return;
  queue.push({ name, props, occurred_at: new Date().toISOString() });
  clearTimeout(flushTimer);
  flushTimer = setTimeout(flush, queue.length >= 20 ? 0 : 3000);
}

async function flush(): Promise<void> {
  if (!queue.length) return;
  const batch = queue.splice(0, queue.length);
  try {
    await getAccessToken();
    const sb = await getSupabase();
    // user_id defaults to auth.uid() server-side; clients can insert but never read events.
    await sb.from('snapcook_events').insert(batch.map((e) => ({ ...e, app_version: config.appVersion })));
  } catch {
    // Analytics must never break the app; drop the batch.
  }
}

if (typeof window !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush();
  });
}
